import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FaArrowLeft, FaPlus } from 'react-icons/fa';
import CommonHeader from '../../util_components/common_header';
import CustomModal from '../../util_components/custom_modal';
import ChecklistForm from './form';
import ReminderForm from '../reminders/ReminderForm';
import {
  create_task,
  current_team_member,
  prime_team_members,
  list_departments
} from '../reminders/api';
import { getCookie } from '../../../util_functions';
import * as actions from '../../../redux/action_creators';

// URL-friendly checkpoint slug (?checkin=…) → backend check_type. Both the
// list page (when linking here) and a hand-typed URL use the slug form.
const CHECKIN_TO_TYPE = {
  day8: 'DAY_8',
  day15: 'DAY_15',
  day22: 'DAY_22',
  week7: 'WEEK_7',
  week11: 'WEEK_11'
};

const LIST_PATH = '/early-retention-checklist';

// Standalone page wrapper around ChecklistForm, opened via
// /early-retention-checklist-form?student_uuid=…&checkin=day8 — replaces the
// former pop-up. The form fills itself out from the query params.
function EarlyRetentionChecklistFormPage() {
  const history = useHistory();
  const dispatch = useDispatch();
  const params = new URLSearchParams(window.location.search);
  // Custom-rule checklists are opened by row uuid (?uuid=…) — a (student,
  // checkin) pair only identifies the five fixed checkpoints.
  const uuid = params.get('uuid') || '';
  const student_uuid = params.get('student_uuid') || '';
  const checkin = (params.get('checkin') || '').toLowerCase();
  const check_type = CHECKIN_TO_TYPE[checkin] || '';
  // Only show "Back to checklist" (and route back on submit) when the page was
  // opened from the checklist list page (?from=list), not from the student profile.
  const from_list = params.get('from') === 'list';

  const go_back = () => history.push(LIST_PATH);

  const invalid = !uuid && (!student_uuid || !check_type);

  const notify = (type, message) =>
    dispatch(actions.set_notification_variable(true, type, message));

  // ── "+ Reminder" floating badge ───────────────────────────────────────────
  // Opens the same create-reminder form as the /reminders dashboard, seeded
  // from this checklist (mirrors the backend's createFollowupReminder: linked
  // to the student, high priority, retention department, link back here).
  const [show_reminder, set_show_reminder] = useState(false);
  // Filled by ChecklistForm once the row loads — carries check_label and the
  // student's name/uuid/slug used to seed the reminder.
  const [checklist_meta, set_checklist_meta] = useState(null);
  // Retention department uuid resolved by name, so manual reminders file
  // under the same department as the auto-generated follow-ups.
  const [retention_department_uuid, set_retention_department_uuid] = useState('');

  useEffect(() => {
    if (invalid) return;
    prime_team_members();
    list_departments().then((rows) => {
      const dept = (rows || []).find((d) => (d.name || '').toLowerCase().includes('retention'));
      if (dept) set_retention_department_uuid(dept.uuid);
    });
  }, [invalid]);

  // Canonical link back to this checklist — same shape the backend's
  // createFollowupReminder puts in its reminder description.
  const checklist_link = useMemo(() => {
    const base = `${window.location.origin}/early-retention-checklist-form`;
    return uuid
      ? `${base}?uuid=${encodeURIComponent(uuid)}`
      : `${base}?student_uuid=${encodeURIComponent(student_uuid)}&checkin=${encodeURIComponent(checkin)}`;
  }, [uuid, student_uuid, checkin]);

  // First line becomes the reminder title; the body notes the source
  // checklist and links back to this form.
  const reminder_initial = useMemo(() => {
    if (!checklist_meta) return undefined;
    const { check_label, student_name, student_slug } = checklist_meta;
    const description = [
      `${check_label || 'Retention'} retention check follow-up for ${student_name || 'student'}(${student_slug || ''})`,
      '',
      `Generated from the "${check_label || ''}" Early Retention Checklist.`,
      `Checklist form link: ${checklist_link}`
    ].join('\n');
    return {
      description,
      priority: 'High',
      department_uuid: retention_department_uuid,
      // Start unassigned (the backend's auto follow-ups are unassigned too) —
      // an empty array here stops ReminderForm defaulting the assignee to self.
      assignee_uuids: []
    };
  }, [checklist_meta, checklist_link, retention_department_uuid]);

  const locked_linked_user = checklist_meta
    ? {
        uuid: checklist_meta.student_uuid,
        name: checklist_meta.student_name,
        role: 'student'
      }
    : null;

  const me = current_team_member(getCookie('cms_email'));

  const handle_reminder_submit = (payload) =>
    create_task(payload)
      .then(() => {
        set_show_reminder(false);
        notify('success', 'Reminder created');
      })
      .catch((e) => {
        notify('error', e?.response?.data?.reason || 'Failed to create reminder');
      });

  return (
    <div>
      <CommonHeader title="Early Retention Checklist" />

      <section
        className="bg-surface-card rounded-lg shadow-sm border border-line"
        style={{ margin: '20px 5%', padding: '20px 24px' }}
      >
        {from_list ? (
          <button
            type="button"
            onClick={go_back}
            className="inline-flex items-center gap-2 mb-4 text-sm font-medium text-blue-600 hover:underline"
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <FaArrowLeft size={12} /> Back to checklist
          </button>
        ) : null}

        {invalid ? (
          <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Invalid link — either a <code>uuid</code> or a valid <code>student_uuid</code> and{' '}
            <code>checkin</code> (day8, day15, day22, week7 or week11) are required.
          </div>
        ) : (
          <ChecklistForm
            uuid={uuid}
            student_uuid={student_uuid}
            check_type={check_type}
            on_submitted={from_list ? go_back : () => {}}
            on_saved_draft={() => {}}
            on_meta_loaded={set_checklist_meta}
          />
        )}
      </section>

      {!invalid && checklist_meta ? (
        <button
          type="button"
          onClick={() => set_show_reminder(true)}
          title="Create a reminder from this checklist"
          style={{
            position: 'fixed',
            bottom: 28,
            right: 28,
            zIndex: 1000,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '11px 22px 11px 13px',
            borderRadius: 9999,
            background: 'var(--brand-primary)',
            color: 'var(--text-inverse)',
            border: 'none',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.25)'
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 26,
              height: 26,
              borderRadius: 9999,
              background: '#ffffff',
              flexShrink: 0
            }}
          >
            <FaPlus size={13} style={{ color: 'var(--brand-primary)' }} />
          </span>{' '}
          Reminder
        </button>
      ) : null}

      <CustomModal
        show={show_reminder}
        close={() => set_show_reminder(false)}
        title="New reminder"
        inner_style={{ width: 'min(640px, 96vw)', maxWidth: 'none' }}
      >
        {show_reminder && checklist_meta ? (
          <ReminderForm
            key={`erc-reminder-${uuid || `${student_uuid}-${checkin}`}`}
            initial={reminder_initial}
            lockedLinkedUser={locked_linked_user}
            selfUuid={me?.uuid}
            onSubmit={handle_reminder_submit}
            onCancel={() => set_show_reminder(false)}
            submitLabel="Create"
          />
        ) : null}
      </CustomModal>
    </div>
  );
}

export default EarlyRetentionChecklistFormPage;
